import { UserCheck, ChevronRight, Edit2, X, Users } from 'lucide-react'
import { useState } from 'react'

import { TeamMemberSelector } from '@/components/pm/issue/team-member-selector'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  useSetStageReviewer,
  useAddStageParticipants,
  useRemoveStageParticipant,
  useStageParticipants,
  getStageDisplayName,
  type WorkflowConfig,
  type WorkflowStage,
  type StageReviewers,
  type ReviewerInfo,
} from '@/hooks'
import { cn } from '@/lib/utils'

interface StageReviewerPanelProps {
  issueId: string
  config: WorkflowConfig
  teamId?: string
  isTeamLeader?: boolean
}

/**
 * 阶段审核人管理面板
 * 显示各阶段的审核人，团队组长可以修改
 */
export function StageReviewerPanel({
  issueId,
  config,
  teamId,
  isTeamLeader = false,
}: StageReviewerPanelProps) {
  const [expanded, setExpanded] = useState(false)
  const [editingStage, setEditingStage] = useState<WorkflowStage | null>(null)
  const [showParticipantsDialog, setShowParticipantsDialog] = useState<WorkflowStage | null>(null)

  const reviewers = config.stageReviewers

  // 获取启用的阶段及其审核人
  const enabledStagesWithReviewers = getEnabledStagesWithReviewers(config, reviewers)

  return (
    <div className="rounded-lg border bg-card">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex w-full items-center justify-between px-4 py-3 text-sm font-medium hover:bg-muted/50"
      >
        <div className="flex items-center gap-2">
          <UserCheck className="h-4 w-4" />
          阶段审核人
          {isTeamLeader && (
            <span className="text-xs text-muted-foreground">(可编辑)</span>
          )}
        </div>
        <ChevronRight
          className={cn('h-4 w-4 transition-transform', expanded && 'rotate-90')}
        />
      </button>

      {expanded && (
        <div className="border-t p-4">
          <div className="space-y-3">
            {enabledStagesWithReviewers.map(({ stage, reviewer }) => (
              <StageReviewerRow
                key={stage}
                issueId={issueId}
                stage={stage}
                reviewer={reviewer}
                teamId={teamId}
                canEdit={isTeamLeader}
                isEditing={editingStage === stage}
                onStartEdit={() => setEditingStage(stage)}
                onEndEdit={() => setEditingStage(null)}
                onManageParticipants={() => setShowParticipantsDialog(stage)}
              />
            ))}
          </div>

          {!isTeamLeader && (
            <p className="mt-3 text-xs text-muted-foreground">
              只有团队组长可以修改阶段审核人
            </p>
          )}
        </div>
      )}

      {/* 参与人员管理对话框 */}
      {showParticipantsDialog && (
        <StageParticipantsDialog
          issueId={issueId}
          stage={showParticipantsDialog}
          teamId={teamId}
          canEdit={isTeamLeader}
          onClose={() => setShowParticipantsDialog(null)}
        />
      )}
    </div>
  )
}

/**
 * 单个阶段审核人行
 */
function StageReviewerRow({
  issueId,
  stage,
  reviewer,
  teamId,
  canEdit,
  isEditing,
  onStartEdit,
  onEndEdit,
  onManageParticipants,
}: {
  issueId: string
  stage: WorkflowStage
  reviewer: ReviewerInfo | null
  teamId?: string
  canEdit: boolean
  isEditing: boolean
  onStartEdit: () => void
  onEndEdit: () => void
  onManageParticipants: () => void
}) {
  const setReviewerMutation = useSetStageReviewer()
  const [selectedReviewerId, setSelectedReviewerId] = useState<string>(reviewer?.id ?? '')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const handleSave = () => {
    setErrorMessage(null)
    console.log('Saving stage reviewer:', { issueId, stage, reviewerId: selectedReviewerId || null })
    setReviewerMutation.mutate(
      {
        issueId,
        stage,
        reviewerId: selectedReviewerId || null,
      },
      {
        onSuccess: () => {
          console.log('Stage reviewer saved successfully')
          onEndEdit()
        },
        onError: (error: Error & { code?: string }) => {
          console.error('Failed to set stage reviewer:', error)
          setErrorMessage(error.message || '保存失败')
        },
      }
    )
  }

  const handleCancel = () => {
    setSelectedReviewerId(reviewer?.id ?? '')
    onEndEdit()
  }

  const handleResetToDefault = () => {
    setReviewerMutation.mutate(
      {
        issueId,
        stage,
        reviewerId: null,
      },
      {
        onSuccess: () => {
          onEndEdit()
        },
      }
    )
  }

  return (
    <div className="flex items-center justify-between rounded-md bg-muted/30 px-3 py-2">
      <div className="flex-1">
        <div className="text-sm font-medium">{getStageDisplayName(stage)}</div>
        {isEditing ? (
          <div className="mt-2 flex items-center gap-2">
            {teamId ? (
              <TeamMemberSelector
                teamId={teamId}
                mode="single"
                value={selectedReviewerId}
                onChange={(value) => setSelectedReviewerId(value as string)}
                placeholder="选择审核人"
                className="flex-1"
              />
            ) : (
              <input
                type="text"
                value={selectedReviewerId}
                onChange={(e) => setSelectedReviewerId(e.target.value)}
                placeholder="输入员工ID"
                className="flex-1 rounded-md border bg-background px-2 py-1 text-sm"
              />
            )}
            <button
              onClick={handleSave}
              disabled={setReviewerMutation.isPending}
              className="rounded-md bg-primary px-3 py-1 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
            >
              {setReviewerMutation.isPending ? '保存中...' : '保存'}
            </button>
            <button
              onClick={handleCancel}
              className="rounded-md border border-input bg-background px-3 py-1 text-sm font-medium hover:bg-accent hover:text-accent-foreground"
            >
              取消
            </button>
            {reviewer && !reviewer.isTeamLeader && (
              <button
                onClick={handleResetToDefault}
                disabled={setReviewerMutation.isPending}
                className="rounded px-2 py-1 text-xs text-blue-600 hover:bg-blue-100 dark:hover:bg-blue-900/30"
                title="恢复默认"
              >
                恢复默认
              </button>
            )}
          </div>
        ) : (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {reviewer ? (
                <>
                  {reviewer.name ?? reviewer.id.slice(0, 8)}
                  {reviewer.isTeamLeader && (
                    <span className="ml-1 text-blue-600">(组长)</span>
                  )}
                </>
              ) : (
                '未设置'
              )}
            </span>
          </div>
        )}
        {/* 错误提示 */}
        {errorMessage && (
          <div className="mt-2 rounded-md bg-destructive/10 px-2 py-1 text-xs text-destructive">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="flex items-center gap-1">
        {/* 管理参与人员按钮 */}
        <button
          onClick={onManageParticipants}
          className="rounded p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          title="管理参与人员"
        >
          <Users className="h-4 w-4" />
        </button>

        {/* 编辑按钮 - 始终显示，无权限时禁用 */}
        {!isEditing && (
          <button
            onClick={canEdit ? onStartEdit : undefined}
            disabled={!canEdit}
            className={cn(
              "rounded p-1.5",
              canEdit
                ? "text-muted-foreground hover:bg-muted hover:text-foreground"
                : "text-muted-foreground/50 cursor-not-allowed"
            )}
            title={canEdit ? "编辑审核人" : "只有团队组长可以编辑"}
          >
            <Edit2 className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  )
}

/**
 * 阶段参与人员管理对话框
 */
function StageParticipantsDialog({
  issueId,
  stage,
  teamId,
  canEdit,
  onClose,
}: {
  issueId: string
  stage: WorkflowStage
  teamId?: string
  canEdit: boolean
  onClose: () => void
}) {
  const { data: participants, isLoading } = useStageParticipants(issueId, stage)
  const addParticipantsMutation = useAddStageParticipants()
  const removeParticipantMutation = useRemoveStageParticipant()
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<string[]>([])
  const [newEmployeeId, setNewEmployeeId] = useState('')

  const handleAddParticipants = () => {
    const ids = teamId ? selectedEmployeeIds : newEmployeeId ? [newEmployeeId] : []
    if (ids.length === 0) {return}

    addParticipantsMutation.mutate(
      { issueId, stage, employeeIds: ids },
      {
        onSuccess: () => {
          setSelectedEmployeeIds([])
          setNewEmployeeId('')
        },
      }
    )
  }

  const handleRemoveParticipant = (employeeId: string) => {
    removeParticipantMutation.mutate({ issueId, stage, employeeId })
  }

  return (
    <AlertDialog open onOpenChange={onClose}>
      <AlertDialogContent className="max-w-md">
        <AlertDialogHeader>
          <AlertDialogTitle>
            {getStageDisplayName(stage)} - 参与人员
          </AlertDialogTitle>
          <AlertDialogDescription>
            管理此阶段的参与人员
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="py-4 space-y-4">
          {/* 当前参与人员列表 */}
          <div>
            <h4 className="text-sm font-medium mb-2">当前参与人员</h4>
            {isLoading ? (
              <div className="text-sm text-muted-foreground">加载中...</div>
            ) : participants && participants.length > 0 ? (
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {participants.map((p) => (
                  <div
                    key={p.id}
                    className="flex items-center justify-between rounded-md bg-muted/50 px-3 py-2"
                  >
                    <span className="text-sm">
                      {p.employeeName || `员工 ${p.employeeId.slice(0, 8)}`}
                    </span>
                    {canEdit && (
                      <button
                        onClick={() => handleRemoveParticipant(p.employeeId)}
                        disabled={removeParticipantMutation.isPending}
                        className="rounded p-1 text-red-600 hover:bg-red-100 dark:hover:bg-red-900/30"
                        title="移除"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-muted-foreground">暂无参与人员</div>
            )}
          </div>

          {/* 添加参与人员 */}
          {canEdit && (
            <div>
              <h4 className="text-sm font-medium mb-2">添加参与人员</h4>
              <div className="flex gap-2">
                {teamId ? (
                  <TeamMemberSelector
                    teamId={teamId}
                    mode="multiple"
                    value={selectedEmployeeIds}
                    onChange={(value) => setSelectedEmployeeIds(value as string[])}
                    placeholder="选择参与人员"
                    className="flex-1"
                  />
                ) : (
                  <input
                    type="text"
                    value={newEmployeeId}
                    onChange={(e) => setNewEmployeeId(e.target.value)}
                    placeholder="输入员工ID"
                    className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
                  />
                )}
                <button
                  onClick={handleAddParticipants}
                  disabled={
                    addParticipantsMutation.isPending ||
                    (teamId ? selectedEmployeeIds.length === 0 : !newEmployeeId.trim())
                  }
                  className="rounded-md bg-blue-600 px-3 py-2 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
                >
                  添加
                </button>
              </div>
            </div>
          )}

          {/* 错误提示 */}
          {(addParticipantsMutation.error || removeParticipantMutation.error) && (
            <div className="rounded-md bg-destructive/10 p-2 text-sm text-destructive">
              {addParticipantsMutation.error?.message || removeParticipantMutation.error?.message}
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>关闭</AlertDialogCancel>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}

/**
 * 获取启用的阶段及其审核人
 */
function getEnabledStagesWithReviewers(
  config: WorkflowConfig,
  reviewers: StageReviewers
): Array<{ stage: WorkflowStage; reviewer: ReviewerInfo | null }> {
  const result: Array<{ stage: WorkflowStage; reviewer: ReviewerInfo | null }> = []

  if (config.productDiscussionEnabled) {
    result.push({ stage: 'product_discussion', reviewer: reviewers.productDiscussion })
  }

  if (config.uiDesignEnabled) {
    result.push({ stage: 'ui_design', reviewer: reviewers.uiDesign })
    result.push({ stage: 'ui_confirmation', reviewer: reviewers.uiConfirmation })
  }

  // 代码审查始终显示
  result.push({ stage: 'code_review', reviewer: reviewers.codeReview })

  // 运维部署（可选）
  if (config.opsDeploymentEnabled) {
    result.push({ stage: 'ops_deployment', reviewer: reviewers.opsDeployment })
  }

  // 测试、产品验收始终显示
  result.push({ stage: 'testing', reviewer: reviewers.testing })
  result.push({ stage: 'product_acceptance', reviewer: reviewers.productAcceptance })

  return result
}
